import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Container,
    Stack,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { RESOURCE_HERO, RESOURCE_POSTS, RESOURCE_TAGS } from '../../content/medzhub-resources/posts';

const FILTER_ORDER = [
    RESOURCE_TAGS.ALL,
    RESOURCE_TAGS.FIELD_VISIBILITY_PAYOUTS,
    RESOURCE_TAGS.DOCUMENTATION_CLAIMS,
    RESOURCE_TAGS.EVALUATING_OPTIONS,
];

const ResourcesPage = () => {
    const [activeTag, setActiveTag] = useState(RESOURCE_TAGS.ALL);

    const visiblePosts = useMemo(() => {
        if (activeTag === RESOURCE_TAGS.ALL) return RESOURCE_POSTS;
        return RESOURCE_POSTS.filter((p) => p.tag === activeTag);
    }, [activeTag]);

    return (
        <>
            <Header />
            <Box
                component="main"
                sx={{
                    py: { xs: 6, md: 10 },
                    minHeight: '50vh',
                    background: 'linear-gradient(180deg, #FFFFFF 0%, #F7F9FF 40%, #FFFFFF 100%)',
                }}
            >
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h1" fontWeight={700} gutterBottom sx={{ maxWidth: 720 }}>
                        {RESOURCE_HERO.title}
                    </Typography>
                    <Typography color="text.secondary" paragraph sx={{ maxWidth: 720, mb: 4 }}>
                        {RESOURCE_HERO.subtitle}
                    </Typography>

                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 4 }}>
                        {FILTER_ORDER.map((label) => (
                            <Chip
                                key={label}
                                label={label}
                                onClick={() => setActiveTag(label)}
                                color={activeTag === label ? 'primary' : 'default'}
                                variant={activeTag === label ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 600 }}
                            />
                        ))}
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                            gap: 3,
                            mb: 8,
                        }}
                    >
                        {visiblePosts.map((post) => (
                            <Card
                                key={post.slug}
                                elevation={0}
                                sx={{
                                    border: '1px solid rgba(15, 23, 42, 0.10)',
                                    borderRadius: 2,
                                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 18px 40px rgba(2, 6, 23, 0.08)',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                <CardActionArea component={Link} to={`/blog/${post.slug}`} sx={{ alignItems: 'stretch', height: '100%' }}>
                                    <CardContent sx={{ p: 3, height: '100%', textAlign: 'left' }}>
                                        <Chip label={post.tag} size="small" sx={{ mb: 1.5, fontWeight: 600 }} />
                                        <Typography variant="h6" component="h2" fontWeight={700} gutterBottom>
                                            {post.title}
                                        </Typography>
                                        <Typography color="text.secondary" variant="body2" paragraph sx={{ mb: 1 }}>
                                            {post.excerpt}
                                        </Typography>
                                        <Typography variant="caption" color="primary" fontWeight={600}>
                                            {post.readMinutes} min read
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            borderRadius: 3,
                            p: { xs: 3, md: 4 },
                            mb: 4,
                            background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
                            border: '1px solid rgba(0, 102, 255, 0.15)',
                        }}
                    >
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Still running field operations on WhatsApp?
                        </Typography>
                        <Typography color="text.secondary" paragraph sx={{ maxWidth: 720 }}>
                            Most centres that come to meDZhub have 8 to 20 therapists, a payment dispute every cycle, and
                            documentation that wouldn't survive an audit. If that's where you are, the 20-minute walkthrough is
                            worth your time.
                        </Typography>
                        <Button component={Link} to="/demo" variant="contained" size="large" data-gtm="Resources-walkthrough">
                            Walk me through it
                        </Button>
                    </Box>

                    <Box sx={{ pb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Prefer to talk directly? Get in touch on WhatsApp.
                        </Typography>
                        <Button
                            component="a"
                            href="https://wa.me/919187644794"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="large"
                        >
                            Talk to us on WhatsApp
                        </Button>
                    </Box>
                </Container>
            </Box>
            <Footer />
        </>
    );
};

export default ResourcesPage;
